import { useState } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { Link } from 'react-router-dom'
import { Plus, Box, X } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import { formatNumber, getInitials } from '../lib/utils'
import LoadingSpinner from '../components/LoadingSpinner'
import Field, { inputClass } from '../components/Field'
import type { Product, Project, UserRef, User } from '../types'

export default function Products() {
  useDocumentTitle('Products')
  const api = useApi()
  const { user } = useAuth()
  const { data, loading, refetch } = useData(() =>
    Promise.all([api.getProducts(), api.getProjects(), api.getUsers()]).then(
      ([products, projects, users]) => ({
        products: (products || []) as Product[],
        projects: (projects || []) as Project[],
        users: (users || []) as User[],
      }),
    ),
  )
  const [showAdd, setShowAdd] = useState(false)
  const [search, setSearch] = useState('')
  const [newName, setNewName] = useState('')
  const [newVendor, setNewVendor] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  if (loading || !data) return <LoadingSpinner />

  const { products, projects, users } = data as {
    products: Product[]
    projects: Project[]
    users: User[]
  }

  // Live directory lookup keyed on Entra oid - keeps the table in sync with
  // any rename in Entra (the snapshot embedded on the product doc is stale
  // until the next time the product is saved).
  const usersByOid = new Map(users.map(u => [u.oid, u]))

  const q = search.trim().toLowerCase()
  const filteredProducts = q
    ? products.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.vendor || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q),
      )
    : products

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name || !user) {
      setCreateError('Name is required')
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const ownerRef: UserRef = { oid: user.id, name: user.name, email: user.email }
      await api.createProduct({
        name,
        vendor: newVendor.trim() || undefined,
        description: newDescription.trim() || undefined,
        ownerRef,
        contributors: [],
      })
      setNewName('')
      setNewVendor('')
      setNewDescription('')
      setShowAdd(false)
      refetch()
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to create product')
    }
    setCreating(false)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Products</h1>
          <p className="text-slate-500 text-sm">Track ongoing health, adoption, and value of our Products</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Box className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-700 mb-1">No products yet</h3>
          <p className="text-sm text-slate-500 mb-4">Add a product to start tracking health, adoption, and value over time.</p>
          <button
            onClick={() => setShowAdd(true)}
            className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      ) : (
        <>
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 uppercase">Filters:</span>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ml-auto bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm w-64"
          />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-[#00AEEF]/5 to-[#005EB8]/5 border-b border-slate-100">
                {['Product', 'Vendor', 'Owner', 'Linked Projects', 'Hrs Realised', 'Hrs Pipeline'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-sm text-slate-400">No products match "{search}"</td>
                </tr>
              )}
              {filteredProducts.map(product => {
                // Roll-up over linked projects: realised = sum of ledger hours on
                // complete projects; pipeline = sum of expectedValue on forecast.
                const linkedAll = projects.filter(p => p.productId === product.id)
                const realisedHrs = linkedAll
                  .filter(p => p.status === 'complete')
                  .reduce((s, p) => {
                    for (const entry of Object.values(p.monthlyLedger || {})) {
                      s += (entry.base ?? 0) + (entry.adjustments ?? 0)
                    }
                    return s
                  }, 0)
                const pipelineHrs = linkedAll
                  .filter(p => p.status === 'forecast')
                  .reduce((s, p) => s + (p.expectedValue || 0), 0)
                const liveOwner = usersByOid.get(product.ownerRef.oid)
                const ownerName = liveOwner?.name || product.ownerRef.name
                const ownerEmail = liveOwner?.email || product.ownerRef.email
                return (
                  <tr key={product.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-5 py-4">
                      <Link to={`/products/${product.id}`} className="font-semibold text-slate-800 hover:text-[#005EB8]">
                        {product.name}
                      </Link>
                      {product.description && (
                        <div className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{product.description}</div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{product.vendor || '-'}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[10px]">
                          {getInitials(ownerEmail || ownerName || 'U')}
                        </div>
                        <span className="text-sm text-slate-700">{ownerName || ownerEmail}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{linkedAll.length}</td>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-700">
                      {realisedHrs > 0 ? formatNumber(realisedHrs) : <span className="text-slate-400 font-normal">-</span>}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      {pipelineHrs > 0 ? formatNumber(pipelineHrs) : <span className="text-slate-400">-</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-800">Add Product</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <Field label="Name">
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className={inputClass}
                  placeholder="Asana"
                  autoFocus
                />
              </Field>
              <Field label="Vendor">
                <input
                  type="text"
                  value={newVendor}
                  onChange={e => setNewVendor(e.target.value)}
                  className={inputClass}
                  placeholder="Asana, Inc."
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={newDescription}
                  onChange={e => setNewDescription(e.target.value)}
                  className={inputClass}
                  rows={3}
                  placeholder="What it's used for"
                />
              </Field>
              {createError && <p className="text-sm text-red-600">{createError}</p>}
              <p className="text-xs text-slate-500">You'll be set as the owner. Ownership can be reassigned later from the product page.</p>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
